import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Search, 
  Filter, 
  Eye, 
  CheckCircle, 
  Trash2, 
  Clock, 
  CreditCard,
  FileText,
  AlertCircle,
  Building2
} from 'lucide-react';

const AdminHub = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchSubmissions();
  }, [filter, user]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?status_filter=${filter}` : '';
      const response = await api.get(`/admin/submissions${params}`);
      setSubmissions(response.data.submissions);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load submissions');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (submissionId) => {
    try {
      await api.post(`/admin/submissions/${submissionId}/mark-paid`);
      toast.success('Invoice marked as paid');
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const handleDelete = async (submissionId) => {
    try {
      await api.delete(`/admin/submissions/${submissionId}`);
      toast.success('Submission deleted');
      setDeleteConfirm(null);
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete submission');
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      sub.venue_name?.toLowerCase().includes(search) ||
      sub.user_name?.toLowerCase().includes(search) ||
      sub.user_email?.toLowerCase().includes(search) ||
      sub.organisation_name?.toLowerCase().includes(search) ||
      sub.submission_id?.toLowerCase().includes(search)
    );
  });

  const getStatusBadge = (submission) => {
    if (submission.payment_status === 'pending_invoice') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          <Clock className="w-3 h-3 mr-1" />
          Pending Invoice
        </span>
      );
    }
    if (submission.payment_status === 'paid' || submission.payment_status === 'succeeded') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Paid
        </span>
      );
    }
    if (submission.is_locked) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Locked
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Draft
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user?.is_admin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold">Admin Hub</h1>
            <p className="text-muted-foreground">Manage submissions and invoices</p>
          </div>
          <div className="text-right text-sm text-muted-foreground">
            <div className="font-medium">{user?.email}</div>
            <div className="text-xs">Administrator</div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card rounded-xl border p-4">
            <div className="text-2xl font-bold">{submissions.length}</div>
            <div className="text-sm text-muted-foreground">Total Submissions</div>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <div className="text-2xl font-bold text-amber-600">
              {submissions.filter(s => s.payment_status === 'pending_invoice').length}
            </div>
            <div className="text-sm text-muted-foreground">Pending Invoice</div>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <div className="text-2xl font-bold text-emerald-600">
              {submissions.filter(s => ['paid', 'succeeded'].includes(s.payment_status)).length}
            </div>
            <div className="text-sm text-muted-foreground">Paid</div>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <div className="text-2xl font-bold text-gray-600">
              {submissions.filter(s => !s.payment_status || s.status === 'draft').length}
            </div>
            <div className="text-sm text-muted-foreground">Drafts</div>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by venue, user, organisation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
              size="sm"
            >
              All
            </Button>
            <Button
              variant={filter === 'pending_invoice' ? 'default' : 'outline'}
              onClick={() => setFilter('pending_invoice')}
              size="sm"
            >
              <Clock className="w-4 h-4 mr-1" />
              Pending
            </Button>
            <Button
              variant={filter === 'paid' ? 'default' : 'outline'}
              onClick={() => setFilter('paid')}
              size="sm"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Paid
            </Button>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-card rounded-xl border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No submissions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Venue</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Submitted By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Organisation</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredSubmissions.map((submission) => (
                    <tr key={submission.submission_id} className="hover:bg-secondary/30 transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium">{submission.venue_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {submission.environment || 'Not set'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm">{submission.user_name}</div>
                        <div className="text-xs text-muted-foreground">{submission.user_email}</div>
                      </td>
                      <td className="px-4 py-4">
                        {submission.organisation_name ? (
                          <div className="text-sm">{submission.organisation_name}</div>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm">{formatDate(submission.locked_at || submission.created_at)}</div>
                      </td>
                      <td className="px-4 py-4">
                        {getStatusBadge(submission)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/submission/${submission.submission_id}`)}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {submission.payment_status === 'pending_invoice' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkPaid(submission.submission_id)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Mark as Paid"
                            >
                              <CreditCard className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(submission.submission_id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Delete Submission?</h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Are you sure you want to delete submission <code className="bg-secondary px-1 rounded">{deleteConfirm}</code>? 
                All associated data will be permanently removed.
              </p>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => handleDelete(deleteConfirm)}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminHub;
